/**
 * Dashboard V2 - Single-column layout with compact rows
 *
 * This is a shell component that receives all onboarding state and data props
 * from DashboardHome.tsx. Child components will be added in later phases.
 */

import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { UploadedFile } from '@/components/files';
import type { ImportStatus } from '@/hooks/integrations/useImportStatus';
import type { RecentImportItem } from '@/hooks/integrations/useRecentImportItems';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCanWrite, useFeatureAccess } from '@/hooks/usePermissions';
import { ensureDashboardPrefetch } from '@/lib/boot/bootRequests';
import { DASHBOARD_RESTRICTED_ENTITY_TYPES } from '@/lib/crm/core/entityTypes';
import { useRestrictedSubscription } from '@/lib/crm/realtime';
import { useOpenNoteCompose } from '@/lib/crm/record/notes/useNoteTabActions';
import { logger } from '@/lib/logger';
import type {
  ProposalDetail,
  ProposalListItem,
} from '@/lib/proposals/core/types';
import { getTriggerActionIds } from '@/lib/proposals/hooks/useTriggerPreview';
import {
  proposalDetailQuery,
  triggerQuery,
} from '@/lib/proposals/query/queries';
import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';
import type { DashboardOnboardingState } from '@/pages/dashboard/DashboardHome';
import type { DashboardMetricsData } from '@/pages/dashboard/hooks/useDashboardData';
import { useRecapMode } from '@/pages/dashboard/hooks/useRecapMode';
import { useStartNewConversation } from '@/pages/reasoning/useStartNewConversation';
import type { CrmObject } from '@/types/crm';

import { ActionsSection } from './ActionsSection';
import { BulkApproveCTA } from './BulkApproveCTA';
import { ConnectEmailCard } from './ConnectEmailCard';
import { MAX_VISIBLE_ACTIONS, UNDO_TIMEOUT_MS } from './constants';
import { DashboardHeader } from './DashboardHeader';
import { DashboardSectionsSkeleton } from './DashboardHomeSkeleton';
import { ImportStatusSection } from './ImportStatusSection';
import { MeetingsSection } from './MeetingsSection';
import { PipelineGlance } from './PipelineGlance';
import { RecapCTA } from './RecapCTA';
import { RecapView } from './RecapView';
import { RecentDealsSection } from './RecentDealsSection';
import { ViewerOverviewStats } from './ViewerOverviewStats';
import { WeeklyRecapView } from './WeeklyRecapView';

/** Props passed from DashboardHome.tsx */
export interface DashboardHomeProps {
  // Onboarding state
  dashboardState: DashboardOnboardingState;
  isProcessing: boolean;
  importStatus: ImportStatus;
  recentImportItems: RecentImportItem[];
  hasOnboardingActions: boolean;
  /** Onboarding actions fetched separately (full ProposalListItem from API) */
  onboardingActions: ProposalListItem[];
  onStartAction: (proposalId: number, contextIds: number[]) => void;
  onSkipAction: (proposalId: number) => void | Promise<void>;
  hasStartedOnboarding: boolean;
  /** First actions are gated on the user creating their deal pipeline. */
  isWaitingForPipeline?: boolean;
  /** Callback to open the CSV import wizard */
  onUploadContacts?: () => void;

  // Data props
  calendar: {
    events: CalendarEvent[];
    integrationEntityIds: string[];
    isLoading: boolean;
    error: string | null;
    reminders: CrmObject[];
    remindersAssociatedObjects: Record<string, CrmObject>;
    tasks: CrmObject[];
    tasksAssociatedObjects: Record<string, CrmObject>;
  };
  actions: {
    items: ProposalListItem[];
    counts: {
      total: number;
      onboarding: number;
      email: number;
      pipeline_update: number;
      qualified_leads: number;
    };
    isLoading: boolean;
    isLoadingRecipientData: boolean;
    error: string | null;
  };
  metrics: DashboardMetricsData;
  greeting: string;
  firstName: string;

  // Org context
  orgId: number;

  // Controlled input state (lifted to DashboardHome wrapper)
  inputValue: string;
  onInputChange: (value: string) => void;
}

// TODO: Extract selection logic to useDashboardSelection() hook and dismissal logic to separate component for better maintainability
function DashboardHome(props: DashboardHomeProps) {
  const {
    dashboardState,
    greeting,
    firstName,
    orgId,
    isProcessing,
    calendar,
    actions,
    importStatus,
    recentImportItems,
    hasOnboardingActions,
    onboardingActions,
    onStartAction,
    onSkipAction,
    hasStartedOnboarding,
    isWaitingForPipeline,
    onUploadContacts: _onUploadContacts,
    metrics,
    inputValue,
    onInputChange,
  } = props;

  // Opt this surface into live CRM updates. `ahoy_meeting` is a restricted type,
  // and the consumer drops restricted-type events for sockets that have not
  // explicitly subscribed — so without this the dashboard's WebSocket connects
  // and heartbeats normally while receiving no meeting events at all. That is
  // what kept the summary CTA from updating until a reload.
  useRestrictedSubscription(DASHBOARD_RESTRICTED_ENTITY_TYPES);

  // Seat gates. A seat without AI Actions gets no recap mode (it is built
  // around actions) and reports no action counts. A read-only seat gets its
  // own layout below (search hero, overview numbers, pipeline, recent deals,
  // upsell last); a full seat's layout is unchanged.
  const { hasFeature } = useFeatureAccess();
  const hasAiActionsFeature = hasFeature('ai_actions');
  const isReadOnlySeat = !useCanWrite();

  // THE timezone this dashboard renders dates in — the user's profile
  // timezone, which the backend always resolves to a valid IANA zone. Never
  // undefined, so nothing downstream needs a fallback.
  const userTimeZone = useDisplayTimezone();

  // Recap mode — end-of-day daily recap + Friday weekly recap CTAs.
  // Gated by `dashboard_recap` feature flag; also disabled during onboarding/processing.
  const isRecapFlagEnabled = useFeatureFlag('dashboard_recap');
  const recap = useRecapMode({
    calendarEvents: calendar.events,
    actions: actions.items,
    orgId,
    timeZone: userTimeZone,
    disabled:
      !isRecapFlagEnabled ||
      !hasAiActionsFeature ||
      hasOnboardingActions ||
      dashboardState.kind !== 'normal',
  });

  // Adding a note from a recap's meeting row opens the shared composer linked
  // to that meeting. Gated by the ahoy_note create grant: a seat without it
  // gets `onAddNote={undefined}`, and RecapView / WeeklyRecapView /
  // MeetingsNeedingNotesList render no "Add summary" affordance when the
  // callback is absent — matching how MeetingsSection withholds it.
  const { openNoteCompose, canCreateNote } = useOpenNoteCompose();
  const handleAddNote = useCallback(
    (meetingEntityId: string) => {
      openNoteCompose({
        linkedEntities: [
          { entityId: meetingEntityId, entityTypeName: 'ahoy_meeting' },
        ],
      });
    },
    [openNoteCompose]
  );
  const onAddNote = canCreateNote ? handleAddNote : undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<Set<number>>(
    new Set()
  );
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
  // Track optimistically dismissed action IDs (hidden immediately, dismissed after undo timeout)
  const [optimisticallyDismissedIds, setOptimisticallyDismissedIds] = useState<
    Set<number>
  >(new Set());
  // ARIA live region announcement for bulk dismiss (A1)
  const [dismissalAnnouncement, setDismissalAnnouncement] = useState('');
  // Version counter to prevent stale setTimeout from clearing a newer announcement
  const announcementVersionRef = useRef(0);
  // Refs for focus restoration after bulk dismiss (A3)
  const firstActionCheckboxRef = useRef<HTMLButtonElement>(null);
  const headerInputRef = useRef<HTMLElement | null>(null);
  const { startNewConversation } = useStartNewConversation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleFilesChange = useCallback((files: UploadedFile[]) => {
    setUploadedFiles(files);
  }, []);

  // Covers client-side navigation to the dashboard (SPA routing from `/`,
  // the org switcher, or any redirect). The hard-load path runs this from
  // prefetch.ts; a client-side arrival would otherwise land with cold caches.
  // The call is idempotent — ensureDashboardPrefetch dedupes by org ID.
  useEffect(() => {
    ensureDashboardPrefetch(orgId, queryClient);
  }, [orgId, queryClient]);

  // Prefetch detail + trigger data for dashboard actions so clicking into one
  // loads instantly. The first action (auto-selected on the action center page)
  // is prefetched immediately; the remaining 4 are deferred 1s to avoid
  // competing with initial render. Each detail prefetch chains into a trigger
  // prefetch once the primary actionId is known from the detail response.
  // React Query deduplicates in-flight requests automatically.
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current || !actions.items.length || !orgId) {
      return;
    }
    const topActions = actions.items.slice(0, MAX_VISIBLE_ACTIONS);

    const prefetchOne = (proposal: ProposalListItem) => {
      queryClient
        .prefetchQuery(proposalDetailQuery(orgId, proposal.id))
        .then(() => {
          const cached = queryClient.getQueryData<ProposalDetail>(
            proposalDetailQuery(orgId, proposal.id).queryKey
          );
          if (cached) {
            const { fetchActionId, cacheActionId } =
              getTriggerActionIds(cached);
            if (fetchActionId) {
              queryClient.prefetchQuery(
                triggerQuery(orgId, proposal.id, fetchActionId, cacheActionId)
              );
            }
          }
        })
        .catch(() => {
          // Best-effort — silently ignore prefetch failures
        });
    };

    // Prefetch first action immediately (it's the auto-selected one)
    prefetchOne(topActions[0]);

    // Defer remaining actions to avoid competing with initial render.
    // Set the guard inside the timeout so that if actions.items changes
    // before it fires (cleanup clears the timer), a future run retries.
    const timer = setTimeout(() => {
      prefetchedRef.current = true;
      for (let i = 1; i < topActions.length; i++) {
        prefetchOne(topActions[i]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [actions.items, orgId, queryClient]);

  // Filter out optimistically dismissed actions for display
  const visibleActions = useMemo(() => {
    const actionItems = actions.items;
    if (optimisticallyDismissedIds.size === 0) {
      return actionItems;
    }
    return actionItems.filter(
      action => !optimisticallyDismissedIds.has(action.id)
    );
  }, [actions.items, optimisticallyDismissedIds]);

  // Same optimistic-dismiss filter for recap mode's today actions, so an action
  // dismissed from the recap view doesn't briefly reappear before the refetch.
  const visibleTodayActions = useMemo(() => {
    const todayItems = recap.todayActions;
    if (optimisticallyDismissedIds.size === 0) {
      return todayItems;
    }
    return todayItems.filter(
      action => !optimisticallyDismissedIds.has(action.id)
    );
  }, [recap.todayActions, optimisticallyDismissedIds]);

  // Wrap onStartAction to pass visible action IDs as context for sidebar auto-advance.
  // In daily recap mode, RecapView shows only visibleTodayActions — using the full
  // visibleActions list as context would silently auto-advance the sidebar into
  // non-recap actions after the user completes/dismisses one from RecapView.
  const handleStartAction = useCallback(
    (proposalId: number) => {
      let contextIds: number[];
      if (hasOnboardingActions) {
        contextIds = onboardingActions
          .filter(a => !optimisticallyDismissedIds.has(a.id))
          .map(a => a.id);
      } else if (recap.isDailyRecapActive) {
        contextIds = visibleTodayActions.map(a => a.id);
      } else {
        contextIds = visibleActions.map(a => a.id);
      }
      onStartAction(proposalId, contextIds);
    },
    [
      hasOnboardingActions,
      onboardingActions,
      optimisticallyDismissedIds,
      recap.isDailyRecapActive,
      visibleTodayActions,
      visibleActions,
      onStartAction,
    ]
  );

  // Toggle selection of an action (supports shift-click for range selection)
  const handleToggleSelection = useCallback(
    (id: number, shiftKey?: boolean) => {
      // Scope shift-click range to the list the user is currently viewing.
      // In daily recap mode, RecapView shows only visibleTodayActions — using the
      // full visibleActions list would silently select hidden non-recap actions.
      const actionItems = recap.isDailyRecapActive
        ? visibleTodayActions
        : visibleActions;

      // Shift-click: select range from last selected to current
      if (shiftKey && lastSelectedId !== null) {
        const lastIndex = actionItems.findIndex(a => a.id === lastSelectedId);
        const currentIndex = actionItems.findIndex(a => a.id === id);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          const rangeIds = actionItems.slice(start, end + 1).map(a => a.id);

          setSelectedActionIds(prev => {
            const next = new Set(prev);
            rangeIds.forEach(rangeId => next.add(rangeId));
            return next;
          });
          return;
        }
      }

      // Regular click: toggle single item
      setSelectedActionIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      setLastSelectedId(id);
    },
    [
      visibleActions,
      visibleTodayActions,
      recap.isDailyRecapActive,
      lastSelectedId,
    ]
  );

  // Clear all selections (also reset shift-click anchor to prevent stale range selection)
  const handleClearSelection = useCallback(() => {
    setSelectedActionIds(new Set());
    setLastSelectedId(null);
  }, []);

  // Approve selected actions
  // Single selection: navigate directly to that action via onStartAction
  // Multiple selections: navigate to actions center (each action triggers navigation,
  // so processing multiple sequentially is not possible)
  const handleApproveSelected = useCallback(() => {
    if (selectedActionIds.size === 1) {
      const firstId = selectedActionIds.values().next().value;
      if (firstId !== undefined) {
        handleStartAction(firstId);
      }
    } else if (selectedActionIds.size > 1) {
      navigate(`/${orgId}/actions`);
    }
    setSelectedActionIds(new Set());
    setLastSelectedId(null);
  }, [selectedActionIds, handleStartAction, navigate, orgId]);

  const handleActivateDailyRecap = useCallback(() => {
    handleClearSelection();
    recap.activateDailyRecap();
  }, [handleClearSelection, recap]);

  const handleActivateWeeklyRecap = useCallback(() => {
    handleClearSelection();
    recap.activateWeeklyRecap();
  }, [handleClearSelection, recap]);

  // Track pending dismissals for undo functionality (keyed by dismissal ID to avoid races)
  const pendingDismissalsRef = useRef<
    Map<string, { ids: number[]; undoClicked: boolean }>
  >(new Map());

  // Dismiss selected actions with undo toast (matches singular dismiss behavior)
  // TODO: Dismissal-with-undo pattern duplicated in ActionRow handleNotNow.
  // CANNOT extract full flow: bulk uses UUID+Map+Set for concurrent operations,
  // single uses timestamp+boolean for simplicity. State machines are fundamentally
  // different. SAFE extraction boundary: only toast construction (duration, messages,
  // action button) could be shared. State management (optimistic updates, undo tracking,
  // error rollback) must remain separate due to different concurrency requirements.
  // These implementations intentionally differ: bulk uses UUID+Map for concurrent multi-operation tracking with partial rollback,
  // single uses timestamp+boolean for simpler single-operation tracking. Full convergence would sacrifice clarity for concurrency patterns.
  // See issue #3389 for extraction plan. If these diverge (e.g., undo duration, retry logic), update BOTH locations.
  const handleDismissSelected = useCallback(async () => {
    const idsToProcess = Array.from(selectedActionIds);
    if (idsToProcess.length === 0) {
      return;
    }

    // Create a unique ID for this dismissal operation
    const dismissalId = crypto.randomUUID();
    pendingDismissalsRef.current.set(dismissalId, {
      ids: idsToProcess,
      undoClicked: false,
    });

    // Clear selection and hide actions immediately for optimistic UI
    setSelectedActionIds(new Set());
    // Reset shift-click anchor to prevent stale range selection
    setLastSelectedId(null);
    setOptimisticallyDismissedIds(prev => {
      const next = new Set(prev);
      idsToProcess.forEach(id => next.add(id));
      return next;
    });

    const count = idsToProcess.length;
    const message =
      count === 1
        ? 'Action dismissed. Undo?'
        : `${count} actions dismissed. Undo?`;

    ++announcementVersionRef.current;
    const currentVersion = announcementVersionRef.current;
    setDismissalAnnouncement(message);

    // Show undo toast
    const toastId = toast(message, {
      duration: UNDO_TIMEOUT_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          const pending = pendingDismissalsRef.current.get(dismissalId);
          if (pending) {
            pending.undoClicked = true;
            // Restore and show actions again
            setOptimisticallyDismissedIds(prev => {
              const next = new Set(prev);
              pending.ids.forEach(id => next.delete(id));
              return next;
            });
          }
          toast.dismiss(toastId);
        },
      },
    });

    // Wait for toast to expire
    await new Promise(resolve => setTimeout(resolve, UNDO_TIMEOUT_MS));

    // Only clear if no newer announcement has been set
    if (announcementVersionRef.current === currentVersion) {
      ++announcementVersionRef.current;
      setDismissalAnnouncement('');
    }

    const pending = pendingDismissalsRef.current.get(dismissalId);

    // If undo was clicked, don't dismiss
    if (pending?.undoClicked) {
      pendingDismissalsRef.current.delete(dismissalId);
      ++announcementVersionRef.current;
      setDismissalAnnouncement('');
      return;
    }

    // Process actual dismissals sequentially, tracking successes for partial rollback.
    // Promise.resolve() normalizes both void and Promise<void> returns from onSkipAction.
    // The try/catch handles rejections from both sync exceptions (thrown immediately)
    // and async promise rejections, ensuring rollback works in all cases.
    const succeededIds: number[] = [];
    try {
      for (const id of idsToProcess) {
        await Promise.resolve(onSkipAction(id));
        succeededIds.push(id);
      }
    } catch (error) {
      // Only rollback IDs that were NOT yet successfully dismissed
      const failedIds = idsToProcess.filter(id => !succeededIds.includes(id));
      logger.error('Failed to dismiss actions', error as Error, {
        component: 'DashboardHome',
        actionIds: failedIds,
        succeededIds,
        count: idsToProcess.length,
      });

      // Announce error to screen readers via existing live region
      const version = ++announcementVersionRef.current;
      setDismissalAnnouncement("We couldn't dismiss those actions. Try again.");

      // Clear announcement after 3 seconds (only if no newer announcement has been set)
      setTimeout(() => {
        if (announcementVersionRef.current === version) {
          setDismissalAnnouncement('');
        }
      }, 3000);

      // Rollback: restore only the failed IDs (succeeded ones stay dismissed)
      setOptimisticallyDismissedIds(prev => {
        const next = new Set(prev);
        failedIds.forEach(id => next.delete(id));
        return next;
      });
      toast.error("We couldn't dismiss some actions. Try again.");
      // Merge only failed IDs back into selection (preserves newer user selections)
      setSelectedActionIds(prev => {
        const next = new Set(prev);
        failedIds.forEach(id => next.add(id));
        return next;
      });
      pendingDismissalsRef.current.delete(dismissalId);
      return;
    }

    // Don't clear optimisticallyDismissedIds on success — the query refetch
    // will remove these items from actions.items. Clearing here would cause
    // a flash-back if the refetch hasn't completed yet. The IDs become
    // harmless no-ops in the filter once the items are gone from the list.
    pendingDismissalsRef.current.delete(dismissalId);

    // Restore focus after bulk dismiss (WCAG 2.4.3)
    // Use .isConnected to check DOM state — avoids stale closure on visibleActions
    // (handleDismissSelected only depends on selectedActionIds/onSkipAction)
    if (firstActionCheckboxRef.current?.isConnected) {
      firstActionCheckboxRef.current.focus();
    } else if (headerInputRef.current) {
      headerInputRef.current.focus();
    }
  }, [selectedActionIds, onSkipAction]);

  // Handle input submission
  const handleSubmit = useCallback(
    async (value: string) => {
      if (!value.trim()) {
        return;
      }
      setIsSubmitting(true);
      try {
        const fileIds = uploadedFiles.map(f => f.id);
        let hadError = false;
        await startNewConversation(
          value,
          (err: string | null) => {
            if (err) {
              hadError = true;
              toast.error(err);
            }
          },
          fileIds
        );
        if (!hadError) {
          setUploadedFiles([]);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [startNewConversation, uploadedFiles, setUploadedFiles, setIsSubmitting]
  );

  const handleDismissAction = useCallback(
    async (id: number) => {
      const wasSelected = selectedActionIds.has(id);
      setSelectedActionIds(prev => {
        if (!prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      try {
        await onSkipAction(id);
      } catch (error) {
        if (wasSelected) {
          setSelectedActionIds(prev => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        }
        throw error;
      }
    },
    [onSkipAction, selectedActionIds]
  );

  const { deactivateRecap } = recap;
  const handleShowAllActions = useCallback(() => {
    deactivateRecap();
    navigate(`/${orgId}/actions`);
  }, [deactivateRecap, navigate, orgId]);

  // Determine which content to show in the actions slot (recap modes swap in on activation)
  let actionsSlot: React.ReactElement;
  if (recap.isWeeklyRecapActive) {
    actionsSlot = (
      <WeeklyRecapView
        weeklyActionsCreated={recap.weeklyActionsCreated}
        weeklyActionsApproved={recap.weeklyActionsApproved}
        mayUndercountCreated={actions.items.length >= 10}
        meetingsNeedingNotes={recap.meetingsNeedingNotes}
        timeZone={userTimeZone}
        onAddNote={onAddNote}
        onShowAllActions={handleShowAllActions}
      />
    );
  } else if (recap.isDailyRecapActive) {
    actionsSlot = (
      <RecapView
        todayActions={visibleTodayActions}
        meetingsNeedingNotes={recap.meetingsNeedingNotes}
        orgId={orgId}
        timeZone={userTimeZone}
        onAddNote={onAddNote}
        onStartAction={handleStartAction}
        onDismissAction={handleDismissAction}
        onShowAllActions={handleShowAllActions}
        selectedIds={selectedActionIds}
        onToggleSelection={handleToggleSelection}
        firstActionCheckboxRef={firstActionCheckboxRef}
      />
    );
  } else {
    actionsSlot = (
      <ActionsSection
        actions={visibleActions as ProposalListItem[]}
        isLoading={actions.isLoading}
        orgId={orgId}
        onboardingOnly={hasOnboardingActions}
        onboardingActions={onboardingActions}
        hasStartedOnboarding={hasStartedOnboarding}
        isWaitingForPipeline={isWaitingForPipeline}
        onStartAction={handleStartAction}
        firstActionCheckboxRef={firstActionCheckboxRef}
        onDismissAction={handleDismissAction}
        selectedIds={selectedActionIds}
        onToggleSelection={handleToggleSelection}
      />
    );
  }

  // The header (greeting, subtitle, input) renders in EVERY state below, so the
  // ConversationInput never remounts as the page moves from loading to content
  // — typed text and focus survive. Only the sections under it switch.
  const isCountsLoading = calendar.isLoading || actions.isLoading;

  // A read-only seat normally has no calendar (viewer memberships skip the
  // mailbox/calendar connect), so "No reminders or meetings today" would be
  // a permanently empty section. Render it only once there is something to
  // show; nothing while it is still loading (no flash of the empty state).
  const hasOwnCalendarContent =
    !calendar.isLoading &&
    (calendar.events.length > 0 ||
      calendar.reminders.length > 0 ||
      calendar.tasks.length > 0);

  // No email integration connected — render the provider-aware ConnectEmailCard
  // in place of normal actions. The card picks Gmail or Outlook based on the
  // provider the user authenticated with.
  if (dashboardState.kind === 'connect-email') {
    return (
      <div className='w-full px-4 sm:px-6 pt-4 sm:pt-12 pb-4 sm:pb-8 mx-auto max-w-[750px]'>
        {/* Header with greeting, subtitle, input bar */}
        <DashboardHeader
          greeting={greeting}
          firstName={firstName}
          isUserLoading={false}
          meetingsCount={metrics.meetingsToday}
          meetingsUpcoming={metrics.meetingsUpcoming}
          actionsCount={hasAiActionsFeature ? actions.counts.total : 0}
          isCountsLoading={isCountsLoading}
          isProcessing={isProcessing}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          inputRef={headerInputRef}
          className='mb-8'
          timeZone={userTimeZone}
          inputValue={inputValue}
          onInputChange={onInputChange}
          orgId={orgId}
          uploadedFiles={uploadedFiles}
          onFilesChange={handleFilesChange}
          isRecapMode={recap.isDailyRecapActive || recap.isWeeklyRecapActive}
          todayActionCount={recap.todayActionCount}
          isReadOnlySeat={isReadOnlySeat}
        />

        {/* Connect Email Card — provider-aware (Gmail or Outlook) */}
        <ConnectEmailCard orgId={String(orgId)} />
      </div>
    );
  }

  // Read-only seat: a cockpit for watching the team, not a queue of work.
  // Numbers first, then the pipeline, then what moved recently; the calendar
  // section only when this seat actually has something in it (a viewer
  // normally has no calendar), and the AI Actions upsell last. Nothing here
  // is a write.
  const readOnlySections = (
    <div className='space-y-6'>
      <ViewerOverviewStats metrics={metrics} orgId={orgId} />

      <PipelineGlance
        metrics={metrics}
        orgId={orgId}
        isProcessing={isProcessing}
      />

      {/* Only once the org has deals at all — otherwise this would repeat
          the pipeline's own "No deals yet" right under it. */}
      {metrics.dealsCount > 0 && <RecentDealsSection orgId={orgId} />}

      {hasOwnCalendarContent && (
        <MeetingsSection
          calendarEvents={calendar.events}
          orgId={orgId}
          integrationEntityIds={calendar.integrationEntityIds}
          isLoading={calendar.isLoading}
          isProcessing={isProcessing}
          timeZone={userTimeZone}
          reminders={calendar.reminders}
          remindersAssociatedObjects={calendar.remindersAssociatedObjects}
          tasks={calendar.tasks}
          tasksAssociatedObjects={calendar.tasksAssociatedObjects}
        />
      )}

      {actionsSlot}
    </div>
  );

  // Normal dashboard content (loading, processing, enriching, imports-starting, normal)
  return (
    <div className='w-full px-4 sm:px-6 pt-4 sm:pt-12 pb-4 sm:pb-8 mx-auto max-w-[750px]'>
      {/* Header with greeting, subtitle, input bar */}
      <DashboardHeader
        greeting={greeting}
        firstName={firstName}
        isUserLoading={false}
        meetingsCount={metrics.meetingsToday}
        meetingsUpcoming={metrics.meetingsUpcoming}
        actionsCount={hasAiActionsFeature ? actions.counts.total : 0}
        isCountsLoading={isCountsLoading || dashboardState.kind === 'loading'}
        isProcessing={isProcessing}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        inputRef={headerInputRef}
        className='mb-8'
        timeZone={userTimeZone}
        inputValue={inputValue}
        onInputChange={onInputChange}
        orgId={orgId}
        uploadedFiles={uploadedFiles}
        onFilesChange={handleFilesChange}
        isRecapMode={recap.isDailyRecapActive || recap.isWeeklyRecapActive}
        todayActionCount={recap.todayActionCount}
        isReadOnlySeat={isReadOnlySeat}
      />

      {/* Onboarding/import status still resolving: hold the sections' shape
          under the real header instead of swapping the whole page. */}
      {dashboardState.kind === 'loading' && (
        <DashboardSectionsSkeleton orgId={orgId} stage='sections-loading' />
      )}
      {dashboardState.kind !== 'loading' && isReadOnlySeat && readOnlySections}
      {dashboardState.kind !== 'loading' && !isReadOnlySeat && (
        /* Single-column content sections */
        <div className='space-y-6'>
          {/* Import Status - shown only during active imports */}
          <ImportStatusSection
            importStatus={importStatus}
            recentItems={recentImportItems}
          />

          {/* Recap CTA — appears at top (under chat input) when triggered */}
          {(recap.shouldShowDailyCTA || recap.shouldShowWeeklyCTA) && (
            <RecapCTA
              showWeeklyCTA={recap.shouldShowWeeklyCTA}
              showDailyCTA={recap.shouldShowDailyCTA}
              meetingsNeedingNotesCount={recap.meetingsNeedingNotesCount}
              todayActionCount={recap.todayActionCount}
              weeklyActionsCreated={recap.weeklyActionsCreated}
              weeklyActionsApproved={recap.weeklyActionsApproved}
              onActivateDaily={handleActivateDailyRecap}
              onActivateWeekly={handleActivateWeeklyRecap}
              onDismissDaily={recap.dismissDailyCTA}
              onDismissWeekly={recap.dismissWeeklyCTA}
            />
          )}

          {/* In recap mode, actions move above meetings */}
          {(recap.isDailyRecapActive || recap.isWeeklyRecapActive) &&
            actionsSlot}

          {/* Meetings section */}
          <MeetingsSection
            calendarEvents={calendar.events}
            orgId={orgId}
            integrationEntityIds={calendar.integrationEntityIds}
            isLoading={calendar.isLoading}
            isProcessing={isProcessing}
            timeZone={userTimeZone}
            reminders={calendar.reminders}
            remindersAssociatedObjects={calendar.remindersAssociatedObjects}
            tasks={calendar.tasks}
            tasksAssociatedObjects={calendar.tasksAssociatedObjects}
          />

          {/* Actions — normal position (below meetings) when not in recap mode */}
          {!recap.isDailyRecapActive &&
            !recap.isWeeklyRecapActive &&
            actionsSlot}

          {/* Bulk selection action bar (appears when items selected) */}
          <BulkApproveCTA
            selectedCount={selectedActionIds.size}
            onApproveSelected={handleApproveSelected}
            onDismissSelected={handleDismissSelected}
            onClearSelection={handleClearSelection}
          />

          {/* ARIA live region for bulk dismiss announcements (A1) */}
          {dismissalAnnouncement && (
            <div role='status' aria-live='polite' className='sr-only'>
              {dismissalAnnouncement}
            </div>
          )}

          {/* Pipeline section (3-column layout) */}
          <PipelineGlance
            metrics={metrics}
            orgId={orgId}
            isProcessing={isProcessing}
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(DashboardHome);
